import { Link } from "react-router-dom";
import { Question, ArrowRight, ChatCircleText } from "@phosphor-icons/react";

const FAQS = [
  {
    section: "Getting started",
    items: [
      {
        q: "Do I need design experience to use this?",
        a: "Not at all. Upload a PNG or JPG of your logo, pick a mode, and we handle the rest. The result is ready to upload straight to your game's livery editor.",
      },
      {
        q: "What file types can I upload?",
        a: "PNG, JPG, and JPEG. Transparent PNGs are fully supported — we'll preserve the alpha channel where the game's editor allows it.",
      },
      {
        q: "Can I upload transparent PNGs?",
        a: "Yes. Transparent regions stay transparent in the output. You can also use the built-in Background Removal tool on the Repair page if your source has a coloured backdrop.",
      },
      {
        q: "How long does conversion take?",
        a: "Typically under 5 seconds. Bulk uploads (Pro plan) can take longer depending on file count.",
      },
    ],
  },
  {
    section: "Credits & billing",
    items: [
      {
        q: "How do conversions work?",
        a: "1 credit = 1 conversion. Every registered account gets 2 free conversions each month (reset on the 1st, no roll-over). Need more? Buy a once-off conversion pack — those credits never expire.",
      },
      {
        q: "What conversion packs are available?",
        a: "Starter (R15) = 5 conversions, Standard (R35) = 15, Pro (R69) = 35 (best value), and Studio (R149) = 100. All are one-time purchases — no subscription, no recurring billing.",
      },
      {
        q: "Do credits expire?",
        a: "Free monthly conversions reset on the 1st of each month and don't roll over. Paid conversions from a pack NEVER expire — buy them once, use them whenever.",
      },
      {
        q: "Is there a subscription?",
        a: "No. Decal Labs is pay-per-pack. You only pay when you want more conversions, and there's nothing to cancel.",
      },
    ],
  },
  {
    section: "Your files & quality",
    items: [
      {
        q: "Why was my SVG rejected by the game?",
        a: "Most game livery editors enforce a strict 15 KB cap and a small list of supported SVG features. Our converter automatically rewrites the file to fit those rules. If a particular SVG is still being rejected, try the Repair tool to strip unsupported elements.",
      },
      {
        q: "What happens if my file is too complex?",
        a: "The converter automatically simplifies very detailed images to fit the 15 KB upload cap. Some fine detail may be smoothed out. Pro users can enable Perfect Vector mode to bypass the cap (recommended for non-game use).",
      },
      {
        q: "What is Perfect Vector mode?",
        a: "A Pro-only toggle that bypasses our 15 KB rescue pipeline, preserving maximum trace fidelity. Output may exceed the game's livery editor cap — ideal for hi-res preview, printing, or any non-game use.",
      },
      {
        q: "Why doesn't my conversion fit the 15 KB limit?",
        a: "Your source is too detailed. Try simplifying it in your editor, use the Split-For-Socials feature to split a complex logo into tiles, or upgrade to Pro for Perfect Vector mode.",
      },
      {
        q: "Why are there upload size limits?",
        a: "Racing-game livery editors typically enforce a 15 KB ceiling per decal. We auto-fit your file to that ceiling so it actually uploads in-game.",
      },
      {
        q: "What makes a decal 'game compatible'?",
        a: "Game livery editors usually accept only path / rect / circle / polyline / polygon SVG nodes, with flat RGB colour and no alpha gradients, filters, masks, scripts, or external image references. Our converter outputs SVGs that follow these rules.",
      },
      {
        q: "Can I repair existing SVG files?",
        a: "Yes — head to the Repair page, upload your SVG, and we'll strip unsupported elements and bring it under the 15 KB cap if possible.",
      },
    ],
  },
  {
    section: "Privacy & ownership",
    items: [
      {
        q: "Is my uploaded content private?",
        a: "Yes. Files are processed in memory and discarded as soon as the response is returned. We never share, sell, or train on your uploads. See our Privacy Policy for the full picture.",
      },
      {
        q: "Will my files be stored permanently?",
        a: "Only if you save them to your account. Uploads themselves are processed and discarded — only your generated SVG (and any source you opt to save) is kept under your account.",
      },
      {
        q: "Can I use decals commercially?",
        a: "You own the SVG output you generate from artwork you have the rights to. You are responsible for ensuring you have the right to upload, convert, and use the source artwork.",
      },
    ],
  },
];

export default function FAQs() {
  return (
    <div className="max-w-[1100px] mx-auto" data-testid="faqs-page">
      {/* Hero */}
      <section className="border-b border-[var(--border)] px-6 md:px-10 py-14 md:py-20">
        <div className="micro-label flex items-center gap-2">
          <Question size={12} weight="bold" /> / Frequently asked
        </div>
        <h1 className="font-display font-black tracking-tight leading-[0.95] text-5xl md:text-6xl mt-3">
          Help &amp; FAQs
        </h1>
        <p className="mt-6 text-[var(--fg-2)] max-w-2xl">
          Short, beginner-friendly answers to the most common questions about
          the converter, our plans, and how your files are handled. Can't find
          what you need?{" "}
          <Link
            to="/contact"
            className="underline font-semibold text-[var(--fg)]"
            data-testid="faqs-contact-link"
          >
            Drop us a message.
          </Link>
        </p>
      </section>

      {/* FAQ sections */}
      {FAQS.map((sec) => (
        <section
          key={sec.section}
          className="border-b border-[var(--border)] px-6 md:px-10 py-12 md:py-16"
        >
          <div className="micro-label">/ {sec.section}</div>
          <h2 className="font-display font-black text-3xl md:text-4xl mt-2">
            {sec.section}
          </h2>
          <div className="mt-8 grid md:grid-cols-2 gap-px bg-[var(--border)] border border-[var(--border)]">
            {sec.items.map((f, i) => (
              <details
                key={i}
                className="group bg-white p-5 cursor-pointer"
                data-testid={`faq-${sec.section.toLowerCase().replace(/\s+/g, "-")}-${i}`}
              >
                <summary className="flex items-start gap-3 list-none">
                  <Question
                    size={18}
                    weight="bold"
                    className="text-[var(--accent)] mt-0.5 shrink-0"
                  />
                  <span className="font-display font-bold text-base flex-1">
                    {f.q}
                  </span>
                  <span className="font-mono text-xs group-open:rotate-45 transition-transform">
                    +
                  </span>
                </summary>
                <p className="text-sm text-[var(--fg-2)] mt-3 pl-8 leading-relaxed">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </section>
      ))}

      {/* CTA — still stuck */}
      <section className="px-6 md:px-10 py-16 md:py-20 text-center">
        <div className="micro-label">/ Still stuck?</div>
        <h2 className="font-display font-black text-3xl md:text-4xl mt-2">
          We're a real team, not a chatbot.
        </h2>
        <p className="mt-4 text-[var(--fg-2)] max-w-xl mx-auto">
          We reply to every support email within one business day.
        </p>
        <Link
          to="/contact"
          className="btn-accent mt-8 inline-flex items-center gap-2"
          data-testid="faqs-cta-contact"
        >
          <ChatCircleText size={14} weight="bold" /> Contact Us
          <ArrowRight size={14} weight="bold" />
        </Link>
      </section>
    </div>
  );
}
